import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, PaperPlaneRight } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChatPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/messages/${userId}`, { withCredentials: true });
      setMessages(prev => {
        // Only update if there are new messages
        if (JSON.stringify(prev) !== JSON.stringify(data)) {
          return data;
        }
        return prev;
      });
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [userId]);

  const fetchOtherUser = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users/${userId}`, { withCredentials: true });
      setOtherUser(data);
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  }, [userId]);

  useEffect(() => {
    const init = async () => {
      await Promise.all([fetchMessages(), fetchOtherUser()]);
      setLoading(false);
    };
    init();
  }, [fetchMessages, fetchOtherUser]);

  // Poll for new messages every 3 seconds
  useEffect(() => {
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    const messageContent = newMessage.trim();
    setNewMessage('');

    try {
      const { data } = await axios.post(`${API_URL}/api/messages`, {
        receiver_id: userId,
        content: messageContent
      }, { withCredentials: true });
      
      // Add message immediately (socket might also add it, we'll handle duplicates)
      setMessages(prev => {
        const exists = prev.some(m => m.id === data.id);
        if (exists) return prev;
        return [...prev, data];
      });
    } catch (error) {
      console.error('Error sending message:', error);
      setNewMessage(messageContent); // Restore message on error
    } finally {
      setSending(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatMessageTime = (isoString) => {
    try {
      return format(new Date(isoString), 'HH:mm');
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="app-container flex items-center justify-center min-h-screen">
        <div className="w-10 h-10 border-4 border-[#9D4CDD] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="app-container flex flex-col" data-testid="chat-page">
      {/* Header */}
      <header className="p-4 border-b border-white/5 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center"
          data-testid="back-button"
        >
          <ArrowLeft size={20} weight="bold" className="text-white" />
        </button>
        
        {otherUser && (
          <div className="flex items-center gap-3">
            {otherUser.avatar ? (
              <img src={otherUser.avatar} alt={otherUser.name} className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="avatar">
                {getInitials(otherUser.name)}
              </div>
            )}
            <div>
              <h2 className="font-semibold text-white">{otherUser.name}</h2>
              {otherUser.location && (
                <p className="text-[#71717A] text-xs">{otherUser.location}</p>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-container">
        {messages.length === 0 ? (
          <div className="text-center py-12 text-[#71717A]">
            <p>No messages yet</p>
            <p className="text-sm mt-1">Start the conversation!</p>
          </div>
        ) : (
          messages.map((message, index) => {
            const isSent = message.sender_id === user?.id;
            return (
              <motion.div
                key={message.id || index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${message.id}`}
              >
                <div className={`chat-bubble ${isSent ? 'sent' : 'received'}`}>
                  <p>{message.content}</p>
                  <p className={`text-[10px] mt-1 ${isSent ? 'text-white/60' : 'text-[#71717A]'}`}>
                    {formatMessageTime(message.created_at)}
                  </p>
                </div>
              </motion.div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="p-4 border-t border-white/5">
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="input-field flex-1"
            data-testid="message-input"
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="w-12 h-12 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] flex items-center justify-center disabled:opacity-50 transition-all hover:shadow-[0_0_20px_rgba(157,76,221,0.4)]"
            data-testid="send-message-button"
          >
            <PaperPlaneRight size={20} weight="fill" className="text-white" />
          </button>
        </div>
      </form>
    </div>
  );
}
